import { useEffect, useState } from 'react';
import { ArrowUpRight, ChevronLeft, ChevronRight, Pause, Play, Database, ShieldCheck, FileText, Sparkles, Search, Check, ArrowRight, Layers, Cpu, ScanSearch, Network, BookOpen, Fingerprint } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import './HomeHero.css';

const slides = [
  {
    label: '信任驱动增长', code: 'TRUST',
    tagline: 'GEO优化公司：前排GEO认为',
    title: 'GEO优化的本质是信任', accent: '信任带来流量转化',
    subtitle: '我们选择基于真实价值，帮品牌赢得AI的长期推荐。',
    primary: '免费咨询', primaryLink: null, secondary: '了解更多', secondaryLink: '/services',
    nodes: ['真实价值', '权威信源', 'AI 推荐'],
  },
  {
    label: '算法洞察能力', code: 'INTELLIGENCE',
    tagline: '技术派GEO优化公司：前排GEO',
    title: '懂算法', accent: '更懂合规',
    subtitle: '深度研究DeepSeek、豆包等6大AI平台信源机制，用技术让品牌被准确理解。',
    primary: 'AI算法情报', primaryLink: '/knowledge', secondary: '查看案例', secondaryLink: '/cases',
    nodes: ['信源研究', '语义理解', '精准引用'],
  },
  {
    label: '合规共建未来', code: 'COMPLIANCE',
    tagline: '工业和信息化部人工智能标准化技术委员会（MIIT/TC1）',
    title: '工作组成员单位', accent: '致力于合规GEO',
    subtitle: '技术派：RAG信源算法研究 + 内容派：结构化内容治理',
    primary: 'GEO知识库', primaryLink: '/knowledge', secondary: 'GEO优化实操', secondaryLink: '/services',
    nodes: ['标准共建', '内容治理', '长期信任'],
  },
];

const sceneContent = [
  { heading: '让真实价值，被 AI 理解', badge: '建立品牌信任', title: '品牌信源', rows: ['企业真实信息', '专业知识内容', '权威信源建设'], result: 'AI 推荐', note: '从品牌价值到长期信任' },
  { heading: '理解算法，让信息准确呈现', badge: '深入信源机制', title: '算法研究', rows: ['平台信源分析', '语义结构优化', '引用准确性追踪'], result: '准确理解', note: '从信源研究到精准引用' },
  { heading: '以合规标准，构建长期价值', badge: '坚持合规治理', title: '内容治理', rows: ['真实价值发现', '结构化内容治理', '持续监测与复盘'], result: '长期信任', note: '从内容治理到品牌资产' },
];

const researchPlatforms = [
  { name: 'DeepSeek', src: '/platform-logos/deepseek.ico', x: 54, y: 78 },
  { name: '豆包', src: '/platform-logos/doubao.png', x: 389, y: 63 },
  { name: '腾讯元宝', src: '/platform-logos/yuanbao.png', x: 461, y: 183 },
  { name: '文心一言', src: '/platform-logos/wenxin.png', x: 390, y: 285 },
  { name: 'Kimi', src: '/platform-logos/kimi.png', x: 91, y: 287 },
  { name: '通义千问', src: '/platform-logos/qianwen.png', x: 9, y: 190 },
];

function ResearchScene() {
  return (
    <div className="geo-scene geo-scene--research" aria-hidden="true">
      <div className="geo-scene__halo" />
      <div className="geo-research__heading"><span>ALGORITHM INSIGHTS</span><strong>多平台信源机制研究</strong></div>
      <div className="geo-research__disc" />
      <svg className="geo-research__network" viewBox="0 0 600 440" fill="none">
        <g stroke="#c493a6" strokeWidth="1.3">{researchPlatforms.map(platform => <path key={platform.name} d={`M285 209 Q${platform.x + 53} 209 ${platform.x + 53} ${platform.y + 29}`} />)}</g>
        <g fill="#bc415d">{researchPlatforms.map(platform => <circle key={platform.name} cx={platform.x + 53} cy={platform.y + 29} r="3" />)}</g>
      </svg>
      <div className="geo-research__chip"><span>RETRIEVAL · RAG</span><Cpu size={35} strokeWidth={1.4} /><strong>AI</strong><small>信源理解</small></div>
      {researchPlatforms.map(platform => <div className="geo-research__platform" key={platform.name} style={{ left: platform.x, top: platform.y }}><img src={platform.src} alt="" /><div><strong>{platform.name}</strong><span>信源机制研究</span></div></div>)}
      <div className="geo-research__semantic"><ScanSearch size={16} /><span>实体识别</span><i /><span>语义关联</span></div>
      <div className="geo-research__pipeline"><div><Database size={17} /><span>信源检索</span></div><ChevronRight size={13} /><div><Network size={17} /><span>语义理解</span></div><ChevronRight size={13} /><div><FileText size={17} /><span>准确引用</span></div></div>
      <div className="geo-scene__caption">懂算法，更懂合规<span>AI 信源研究示意</span></div>
    </div>
  );
}

function ComplianceScene() {
  return (
    <div className="geo-scene geo-scene--compliance" aria-hidden="true">
      <div className="geo-scene__halo" />
      <div className="geo-compliance__back-sheet" />
      <div className="geo-compliance__document">
        <div className="geo-compliance__document-header"><BookOpen size={19} /><span>QIANPAI · GOVERNANCE</span><span>GEO</span></div>
        <small>从真实企业价值出发</small><h3>合规内容治理</h3><p>让每一份品牌信息，经得起验证</p>
        <div className="geo-compliance__rule" />
        <div className="geo-compliance__checklist">{[
          ['真实信源', '企业信息与业务价值'], ['结构化表达', '清晰、准确的内容组织'], ['合规治理', '持续校验内容真实性'], ['追踪复盘', '监测引用与信息准确性'],
        ].map(([title, note]) => <div key={title}><span><Check size={15} /></span><div><strong>{title}</strong><small>{note}</small></div></div>)}</div>
        <div className="geo-compliance__document-footer"><Fingerprint size={20} /><span>真实 · 合规 · 可追溯</span></div>
      </div>
      <div className="geo-compliance__standard"><Layers size={22} /><div><strong>标准共建</strong><span>以专业研究推动规范实践</span></div></div>
      <div className="geo-compliance__shield"><ShieldCheck size={125} strokeWidth={1.1} /><strong>TRUST</strong><span>以合规建立长期信任</span></div>
      <div className="geo-compliance__method"><div className="geo-compliance__method-title">D.S.G.M.<span>合规工作法</span></div><div className="geo-compliance__steps">{[['D', '价值发现'], ['S', '结构化治理'], ['G', '合规治理'], ['M', '持续监测']].map(([letter, title]) => <div key={letter}><strong>{letter}</strong><span>{title}</span></div>)}</div></div>
      <div className="geo-scene__caption">技术研究 × 内容治理<span>合规服务体系示意</span></div>
    </div>
  );
}
function BrandScene({ index }: { index: number }) {
  if (index === 1) return <ResearchScene />;
  if (index === 2) return <ComplianceScene />;
  const scene = sceneContent[index];
  return (
    <div className="geo-scene" aria-hidden="true">
      <div className="geo-scene__halo" />
      <div className="geo-scene__ribbon" />
      <div className="geo-scene__window">
        <div className="geo-scene__chrome"><span /><span /><span /><div><Search size={10} /> QIANPAI · GEO</div><Layers size={15} /></div>
        <div className="geo-scene__workspace">
          <div className="geo-scene__sidebar"><strong>前排<span>GEO</span></strong><i /><span className="selected"><Database size={12} /> 品牌信源</span><span><FileText size={12} /> 内容治理</span><span><ShieldCheck size={12} /> 合规监测</span><div className="geo-scene__sidebar-lines"><i /><i /><i /></div></div>
          <div className="geo-scene__content">
            <div className="geo-scene__workspace-title"><span>品牌与 AI 的信任连接</span><span>GEO</span></div>
            <h3>{scene.heading}</h3>
            <div className="geo-scene__modules">
              <div className="geo-scene__module"><span className="geo-scene__module-icon"><Database size={20} /></span><strong>{scene.title}</strong><div className="geo-scene__lines"><i /><i /><i /></div><small>真实 · 可验证 · 可追溯</small></div>
              <div className="geo-scene__module geo-scene__module--core"><div className="geo-scene__ai">AI</div><strong>结构化理解</strong><span className="geo-scene__core-dots"><i /><i /><i /></span></div>
            </div>
            <div className="geo-scene__rows">{scene.rows.map((row, i) => <div key={row}><span>0{i + 1}</span>{row}<Check size={13} /></div>)}</div>
          </div>
        </div>
      </div>
      <div className="geo-scene__trust"><span><ShieldCheck size={24} /></span><div><strong>{scene.badge}</strong><small>基于真实企业价值</small></div><Check size={16} /></div>
      <div className="geo-scene__answer"><div><Sparkles size={15} /> {scene.result}</div><strong>让品牌成为可信答案</strong><div className="geo-scene__answer-lines"><i /><i /></div><span><ShieldCheck size={12} /> 权威信源支持</span></div>
      <div className="geo-scene__platforms"><span>多平台<br /><strong>信源研究</strong></span>{[
        { src: '/platform-logos/deepseek.ico', label: 'DeepSeek' },
        { src: '/platform-logos/doubao.png', label: '豆包' },
        { src: '/platform-logos/yuanbao.png', label: '元宝' },
        { src: '/platform-logos/kimi.png', label: 'Kimi' },
        { src: '/platform-logos/qianwen.png', label: '千问' },
      ].map(platform => <div key={platform.label}><img src={platform.src} alt="" /><small>{platform.label}</small></div>)}<ArrowRight size={17} /></div>
      <div className="geo-scene__caption">{scene.note}<span>GEO 服务流程示意</span></div>
    </div>
  );
}
export default function HomeHero({ onConsult }: { onConsult: () => void }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(false);
  const reducedMotion = useReducedMotion();
  const playing = !paused && !hovered && !focused && !hidden && !reducedMotion;
  const slide = slides[index];

  useEffect(() => {
    const update = () => setHidden(document.hidden);
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setTimeout(() => setIndex((value) => (value + 1) % slides.length), 6500);
    return () => window.clearTimeout(timer);
  }, [index, playing]);

  return (
    <section className="geo-hero" aria-label="前排科技核心优势" aria-roledescription="轮播图"
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false); }}>
      <div className="geo-hero__grid" aria-hidden="true" />
      <div className="geo-hero__inner">
        {/* Replace copy and artwork together, including stale preview DOM after hot updates. */}
        <div className="geo-hero__main" key={slide.code}>
          <div className="geo-hero__copy" aria-live={playing ? 'off' : 'polite'} aria-atomic="true">
            <div className="geo-hero__eyebrow"><span /> QIANPAI TECHNOLOGY · GEO <span className="geo-hero__edition">/ 0{index + 1}</span></div>
            <p className="geo-hero__tagline">{slide.tagline}</p>
            <h1>{slide.title}<span>{slide.accent}</span></h1>
            <p className="geo-hero__description">{slide.subtitle}</p>
            <div className="geo-hero__actions">
              {slide.primaryLink ? <Link className="geo-hero__primary" to={slide.primaryLink}>{slide.primary}<ArrowUpRight size={18} /></Link> : <button className="geo-hero__primary" onClick={onConsult}>{slide.primary}<ArrowUpRight size={18} /></button>}
              <Link className="geo-hero__secondary" to={slide.secondaryLink}>{slide.secondary}<ChevronRight size={16} /></Link>
            </div>
            <div className="geo-hero__principles"><span>真实价值</span><i /><span>技术驱动</span><i /><span>长期信任</span></div>
          </div>
          <BrandScene index={index} />
        </div>
        <div className="geo-hero__bottom">
          <div className="geo-hero__tabs" role="group" aria-label="选择轮播内容">
            {slides.map((item, i) => <button key={item.code} className={`geo-hero__tab ${index === i ? 'is-active' : ''}`} aria-current={index === i ? 'true' : undefined} onClick={() => setIndex(i)}><span>0{i + 1}</span>{item.label}<span className="geo-hero__tab-line" /></button>)}
          </div>
          <div className="geo-hero__controls">
            <span className="geo-hero__count">0{index + 1}<span> / 03</span></span>
            <button onClick={() => setIndex((index + slides.length - 1) % slides.length)} aria-label="上一张"><ChevronLeft size={18} /></button>
            <button onClick={() => setPaused(!paused)} aria-label={paused ? '播放轮播' : '暂停轮播'} aria-pressed={paused}>{paused ? <Play size={14} /> : <Pause size={14} />}</button>
            <button onClick={() => setIndex((index + 1) % slides.length)} aria-label="下一张"><ChevronRight size={18} /></button>
          </div>
        </div>
      </div>
    </section>
  );
}
